import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LineChart, Line, ResponsiveContainer, XAxis, Tooltip } from "recharts";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button, Card, Pill, Spinner } from "@/components/ui-kit";
import { APP, gradeShort, gradeLabel, GRADE_OPTIONS } from "@/config";
import { Plus, LogOut, ArrowRight, Clock, MessagesSquare, AlertTriangle, ShieldCheck, TrendingUp, ChevronDown, Mail, Send, HelpCircle, Link2, Trophy, Crown } from "lucide-react";
import OnboardingTour from "@/components/OnboardingTour";
import MilestoneShareModal from "@/components/MilestoneShareModal";
import PlanModal from "@/components/PlanModal";
import AccessBanner from "@/components/AccessBanner";
import { PARENT_STEPS } from "@/components/onboardingSteps";

export default function ParentDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(null);
  const [recap, setRecap] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [shareFor, setShareFor] = useState(null); // student {id, name} to pick a win for
  const [shareCard, setShareCard] = useState(null); // minted milestone card payload
  const [showPlans, setShowPlans] = useState(false);
  const [welcomeMode] = useState(() => params.get("welcome") === "1");

  const load = () => api.get("/parent/dashboard").then(({ data }) => setData(data.students)).catch(() => setData([]));
  useEffect(() => { load(); }, []);

  // First-time Google parent nudge: open the "add your first learner" flow (and don't stack the tour).
  useEffect(() => {
    if (welcomeMode) {
      setAdding(true);
      toast.success("Welcome to NumberSense! Let's add your first learner.");
      localStorage.setItem(`ns_tour_parent_${user.id}`, "1");
      params.delete("welcome");
      setParams(params, { replace: true });
    }
  }, []);

  const canLinkGoogle = user.role === "parent" && user.auth_provider !== "google" && !user.google_email;
  const linkGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/parent?link=1";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (!data) return <div className="band-3-5 min-h-screen bg-[var(--ns-bg)]"><Spinner label="Loading dashboard" /></div>;

  return (
    <div className="band-3-5 min-h-screen bg-[var(--ns-bg)]">
      <header className="border-b border-[var(--ns-border)] bg-[var(--ns-surface)]">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[var(--ns-primary)] text-white grid place-items-center font-head font-bold text-sm">N</div>
            <span className="font-head font-bold text-[var(--ns-text)]">{APP.name}</span>
          </div>
          <div className="flex items-center gap-3">
            {user.role === "parent" && (
              user.subscription?.status === "active" ? (
                <button data-testid="manage-plan-btn" onClick={() => setShowPlans(true)} className="flex items-center gap-1.5" title="Manage your plan">
                  <Pill tone="gold"><Crown size={13} /> {user.subscription.plan_label || "Subscribed"}</Pill>
                </button>
              ) : (
                <Button variant="subtle" data-testid="upgrade-btn" onClick={() => setShowPlans(true)}><Crown size={16} /> <span className="hidden sm:inline">Upgrade</span></Button>
              )
            )}
            {user.role === "parent" && (
              <button data-testid="how-it-works-btn" onClick={() => setShowTour(true)} className="flex items-center gap-1 text-sm font-semibold text-[var(--ns-primary)]" title="How NumberSense works">
                <HelpCircle size={18} /> <span className="hidden sm:inline">How it works</span>
              </button>
            )}
            {canLinkGoogle && (
              <button data-testid="connect-google-btn" onClick={linkGoogle} className="flex items-center gap-1 text-sm font-semibold text-[var(--ns-primary)]" title="Connect your Google account for one-tap login">
                <Link2 size={18} /> <span className="hidden sm:inline">Connect Google</span>
              </button>
            )}
            <span className="text-sm text-[var(--ns-muted)] hidden sm:inline">{user.name}</span>
            <Button variant="ghost" data-testid="logout-btn" onClick={() => { logout(); nav("/"); }}><LogOut size={16} /> Log out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-8">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <h1 className="font-head text-3xl font-bold text-[var(--ns-text)]">{user.role === "teacher" ? "Teacher dashboard" : "Family dashboard"}</h1>
            <p className="text-[var(--ns-muted)] mt-1">{user.role === "teacher" ? "The students assigned to you — watch gaps close and see what's next." : "Watch gaps close and see exactly what's next."}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="weekly-recap-btn" onClick={() => setRecap(true)}><Mail size={16} /> Weekly recap</Button>
            {user.role === "parent" && <Button data-testid="add-student-btn" onClick={() => setAdding(true)}><Plus size={18} /> Add student</Button>}
          </div>
        </div>

        {user.role === "parent" && <AccessBanner onUpgrade={() => setShowPlans(true)} />}

        {data.length === 0 && (
          <Card className="p-10 text-center">
            {user.role === "teacher" ? (
              <p className="text-[var(--ns-muted)]" data-testid="teacher-empty-state">No students assigned yet. An administrator will assign learners to you.</p>
            ) : (
              <>
                <p className="text-[var(--ns-muted)]">No students yet. Add your first learner to begin.</p>
                <Button className="mt-4 mx-auto" onClick={() => setAdding(true)}><Plus size={18} /> Add student</Button>
              </>
            )}
          </Card>
        )}

        <div className="grid gap-5">
          {data.map((s) => (
            <StudentCard key={s.student.id} s={s} open={open === s.student.id}
              onToggle={() => setOpen(open === s.student.id ? null : s.student.id)}
              onEnter={() => nav(`/student/${s.student.id}/today`)}
              onShare={() => setShareFor(s.student)}
              onAck={() => load()} />
          ))}
        </div>
      </main>

      {adding && <AddStudent onClose={() => setAdding(false)} onAdded={() => { setAdding(false); load(); }} onUpgrade={() => setShowPlans(true)} />}
      {recap && <WeeklyRecap onClose={() => setRecap(false)} />}
      {shareFor && <ShareWins student={shareFor} onClose={() => setShareFor(null)} onPick={(card) => { setShareFor(null); setShareCard(card); }} />}
      {shareCard && <MilestoneShareModal card={shareCard} onClose={() => setShareCard(null)} />}
      {showPlans && <PlanModal userId={user.id} currentPlan={user.subscription?.plan} subscribed={user.subscription?.status === "active"} onClose={() => setShowPlans(false)} />}
      {user.role === "parent" && (
        <OnboardingTour steps={PARENT_STEPS} storageKey={`ns_tour_parent_${user.id}`} suppressAuto={welcomeMode} forceOpen={showTour} onClose={() => setShowTour(false)} />
      )}
    </div>
  );
}

function WeeklyRecap({ onClose }) {
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get("/parent/weekly-summary").then(({ data }) => setData(data)); }, []);

  const sendIt = async () => {
    setSending(true);
    try {
      const { data: res } = await api.post("/parent/weekly-summary/send");
      if (res.status === "sent") toast.success(`Weekly recap emailed to ${res.recipient}`);
      else if (res.status === "not_configured") toast.info("Email isn't connected yet — add your Resend API key to enable delivery. Your recap is shown here for now.");
      else toast.error("Couldn't send the email. Please try again.");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-5 overflow-y-auto" onClick={onClose}>
      <Card className="w-full max-w-lg p-6 ns-rise my-6" onClick={(e) => e.stopPropagation()} data-testid="weekly-recap-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-head text-xl font-bold text-[var(--ns-text)]">Weekly recap</h3>
          <Pill tone={data?.email_configured ? "mastered" : "muted"}>{data?.email_configured ? "Email ready" : "Preview"}</Pill>
        </div>
        <p className="text-sm text-[var(--ns-muted)] mb-4">A Friday snapshot of each learner's week.</p>
        {!data ? <Spinner label="Building recap" /> : (
          <div className="grid gap-3 max-h-[50vh] overflow-y-auto ns-scroll pr-1">
            {data.students.map((s) => (
              <div key={s.student_id} className="rounded-[var(--ns-radius)] border border-[var(--ns-border)] p-4" data-testid={`recap-${s.student_id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-head font-bold">{s.name} <span className="text-xs font-medium text-[var(--ns-subtle)]">· {s.grade_label}</span></span>
                  <Pill tone="gold">🔥 {s.streak}</Pill>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                  <MiniStat label="Closed" value={s.closed_this_week.length} />
                  <MiniStat label="Minutes" value={s.time_on_task_min} />
                  <MiniStat label="Mastered" value={`${s.mastered_count}/${s.total_skills}`} />
                </div>
                <div className="mt-3 text-xs text-[var(--ns-muted)]">Up next: <span className="text-[var(--ns-text)]">{s.next_skills.slice(0, 2).join(", ") || "Plan complete"}</span></div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button data-testid="send-recap-btn" disabled={sending || !data} onClick={sendIt}><Send size={16} /> {sending ? "Sending…" : "Email it to me"}</Button>
        </div>
      </Card>
    </div>
  );
}

const MiniStat = ({ label, value }) => (
  <div className="rounded-lg bg-[var(--ns-surface-subtle)] py-2">
    <div className="font-head text-lg font-bold text-[var(--ns-text)]">{value}</div>
    <div className="text-[10px] uppercase tracking-wide text-[var(--ns-subtle)] font-semibold">{label}</div>
  </div>
);

function StudentCard({ s, open, onToggle, onEnter, onShare, onAck }) {
  const pct = s.total_skills ? Math.round((100 * s.mastered_count) / s.total_skills) : 0;
  const hasWins = s.mastered_count > 0 || (s.student.streak || 0) >= 3;
  return (
    <Card className="p-5 sm:p-6 ns-rise" data-testid={`student-card-${s.student.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-head text-xl font-bold text-[var(--ns-text)]">{s.student.name}</h3>
            <Pill tone="primary">{gradeShort(s.student.grade)}</Pill>
            {s.student.streak > 0 && <Pill tone="gold">🔥 {s.student.streak}-day streak</Pill>}
          </div>
          {!s.diagnostic_done && <p className="text-sm text-[#B23A28] mt-1">Diagnostic not taken yet</p>}
        </div>
        <div className="flex items-center gap-2">
          {hasWins && (
            <Button variant="outline" data-testid={`share-win-${s.student.id}`} onClick={onShare}><Trophy size={16} /> Share a win</Button>
          )}
          <Button variant="subtle" data-testid={`enter-student-${s.student.id}`} onClick={onEnter}>Open space <ArrowRight size={16} /></Button>
        </div>
      </div>

      {s.alerts?.length > 0 && (
        <div className="mt-4 rounded-[var(--ns-radius)] bg-[#FDECE8] border border-[#F3C9BF] p-3 flex items-start gap-2" data-testid="parent-alert">
          <AlertTriangle size={18} className="text-[#B23A28] mt-0.5" />
          <div className="flex-1 text-sm text-[#8A2C1D]">{s.alerts[0].message}</div>
          <button className="text-xs font-semibold text-[#B23A28]" data-testid="ack-alert" onClick={() => api.post(`/alerts/${s.alerts[0].id}/read`).then(onAck)}>Dismiss</button>
        </div>
      )}

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Metric icon={ShieldCheck} label="Skills mastered" value={`${s.mastered_count}/${s.total_skills}`} sub={`${pct}%`} />
        <Metric icon={TrendingUp} label="Closed this week" value={s.closed_this_week.length} />
        <Metric icon={Clock} label="Time on task" value={`${s.time_on_task_min}m`} />
        <Metric icon={MessagesSquare} label="Tutor sessions" value={s.tutor_sessions} />
      </div>

      <button data-testid={`toggle-details-${s.student.id}`} onClick={onToggle} className="mt-4 flex items-center gap-1 text-sm font-semibold text-[var(--ns-primary)]">
        {open ? "Hide" : "View"} details <ChevronDown size={16} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>

      {open && (
        <div className="mt-4 grid md:grid-cols-2 gap-6 border-t border-[var(--ns-border)] pt-5">
          <div>
            <SectionTitle>Weekly activity</SectionTitle>
            <div className="h-28">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={s.trend}>
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: "var(--ns-subtle)" }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v) => [`${v} min`, "Time"]} />
                  <Line type="monotone" dataKey="minutes" stroke="var(--ns-primary)" strokeWidth={2.5} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div>
            <SectionTitle>Current gaps</SectionTitle>
            {s.gaps.length ? (
              <div className="flex flex-wrap gap-2">{s.gaps.slice(0, 6).map((g) => <Pill key={g.skill_id} tone="gap">{g.name}</Pill>)}</div>
            ) : <p className="text-sm text-[var(--ns-muted)]">No open gaps — nicely on track.</p>}
            <SectionTitle className="mt-4">Up next</SectionTitle>
            {s.next_skills.length ? (
              <ol className="text-sm text-[var(--ns-text)] list-decimal ml-4 space-y-1">{s.next_skills.map((n, i) => <li key={i}>{n}</li>)}</ol>
            ) : <p className="text-sm text-[var(--ns-muted)]">Plan complete.</p>}
          </div>
        </div>
      )}
    </Card>
  );
}

function Metric({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-[var(--ns-radius)] bg-[var(--ns-surface-subtle)] p-3">
      <div className="flex items-center gap-1 text-[var(--ns-subtle)]"><Icon size={14} /><span className="text-xs font-semibold uppercase tracking-wide">{label}</span></div>
      <div className="mt-1 font-head text-2xl font-bold text-[var(--ns-text)]">{value} {sub && <span className="text-sm text-[var(--ns-muted)] font-medium">{sub}</span>}</div>
    </div>
  );
}

const SectionTitle = ({ children, className = "" }) => (
  <div className={`text-xs font-semibold uppercase tracking-wider text-[var(--ns-subtle)] mb-2 ${className}`}>{children}</div>
);

function ShareWins({ student, onClose, onPick }) {
  const [opts, setOpts] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    api.get(`/students/${student.id}/share-options`).then(({ data }) => setOpts(data)).catch(() => setOpts({ options: [] }));
  }, [student.id]);

  const pick = async (o) => {
    setBusy(o.kind + o.ref);
    try {
      const { data } = await api.post(`/students/${student.id}/milestones`, { kind: o.kind, ref: o.ref, title: o.title, subtitle: o.subtitle, icon: o.icon });
      onPick(data);
    } catch { toast.error("Couldn't create the card. Please try again."); } finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-5 overflow-y-auto" onClick={onClose}>
      <Card className="w-full max-w-md p-6 ns-rise my-6" onClick={(e) => e.stopPropagation()} data-testid="share-wins-modal">
        <h3 className="font-head text-xl font-bold text-[var(--ns-text)] mb-1">Celebrate {student.name}'s wins</h3>
        <p className="text-sm text-[var(--ns-muted)] mb-4">Pick an achievement to turn into a shareable card.</p>
        {!opts ? <Spinner label="Finding wins" /> : (
          opts.options.length === 0 ? (
            <p className="text-sm text-[var(--ns-muted)] py-6 text-center" data-testid="no-wins">No shareable wins yet — master a skill or build a streak first!</p>
          ) : (
            <div className="grid gap-2 max-h-[55vh] overflow-y-auto ns-scroll pr-1">
              {opts.options.map((o, i) => (
                <button key={o.kind + o.ref} data-testid={`share-option-${i}`} onClick={() => pick(o)} disabled={!!busy}
                  className="text-left rounded-[var(--ns-radius)] border-2 border-[var(--ns-border)] hover:border-[var(--ns-primary)] p-3 flex items-center justify-between gap-3 transition-colors disabled:opacity-60">
                  <div className="min-w-0">
                    <div className="font-head font-semibold truncate">{o.title}</div>
                    <div className="text-xs text-[var(--ns-muted)] truncate">{o.subtitle}</div>
                  </div>
                  <Pill tone={o.kind === "badge" ? "gold" : "mastered"}>{o.kind === "badge" ? "Badge" : "Skill"}</Pill>
                </button>
              ))}
            </div>
          )
        )}
        <div className="mt-5 flex justify-end"><Button variant="ghost" onClick={onClose}>Close</Button></div>
      </Card>
    </div>
  );
}

function AddStudent({ onClose, onAdded, onUpgrade }) {
  const [form, setForm] = useState({ name: "", grade: 5, daily_time_budget: 15, consent_granted: false });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Please enter a name");
    if (!form.consent_granted) return toast.error("Parent consent is required for student accounts");
    setLoading(true);
    try {
      await api.post("/students", form);
      toast.success("Student added");
      onAdded();
    } catch (e) {
      if (e.response?.status === 402) { onClose(); }
      else toast.error(apiErr(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-5" onClick={onClose}>
      <Card className="w-full max-w-md p-6 ns-rise" onClick={(e) => e.stopPropagation()} data-testid="add-student-modal">
        <h3 className="font-head text-xl font-bold text-[var(--ns-text)] mb-4">Add a student</h3>
        <div className="grid gap-4">
          <div>
            <label className="text-sm font-semibold">Name</label>
            <input data-testid="new-student-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full rounded-[var(--ns-radius)] border-2 border-[var(--ns-border)] px-4 py-3 bg-[var(--ns-surface)]" />
          </div>
          <div>
            <label className="text-sm font-semibold">Grade</label>
            <select data-testid="new-student-grade" value={form.grade} onChange={(e) => setForm({ ...form, grade: Number(e.target.value) })}
              className="mt-1 w-full rounded-[var(--ns-radius)] border-2 border-[var(--ns-border)] px-3 py-3 bg-[var(--ns-surface)]">
              {GRADE_OPTIONS.map((g) => <option key={g} value={g}>{gradeLabel(g)}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold">Daily time budget</label>
            <select data-testid="new-student-budget" value={form.daily_time_budget} onChange={(e) => setForm({ ...form, daily_time_budget: Number(e.target.value) })}
              className="mt-1 w-full rounded-[var(--ns-radius)] border-2 border-[var(--ns-border)] px-3 py-3 bg-[var(--ns-surface)]">
              {[10, 15, 20, 30].map((m) => <option key={m} value={m}>{m} minutes</option>)}
            </select>
          </div>
          <label className="flex items-start gap-2 text-sm text-[var(--ns-muted)]">
            <input type="checkbox" data-testid="consent-checkbox" checked={form.consent_granted} onChange={(e) => setForm({ ...form, consent_granted: e.target.checked })} className="mt-1" />
            I am the parent/guardian and grant consent for this under-13 student account.
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="save-student-btn" disabled={loading} onClick={submit}>{loading ? "Adding…" : "Add student"}</Button>
        </div>
      </Card>
    </div>
  );
}
